import {
  drybulkCargoOperationsService,
  type IncrementCompleteRequest,
  type IncrementStartRequest,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { DRYBULK_CARGO_OPS_QUERY_KEYS, DRYBULK_CARGO_OPS_TELEMETRY } from "../constants";

const context = (action: string, operationId: string) => ({
  module: DRYBULK_CARGO_OPS_TELEMETRY.module,
  screen: DRYBULK_CARGO_OPS_TELEMETRY.sequencesScreen,
  action,
  entityType: "cargo_operation",
  entityId: operationId,
});

/**
 * DBCO-0048 (R-20..R-22) - start (the first or a further increment) and complete an increment. The server decides every rule and
 * its refusals are rendered by the caller; on success the detail (status, sequences, rollups) is refetched.
 */
export const useIncrementMutations = (operationId: string) => {
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId) });
  const start = useMutation({
    mutationFn: ({ sequenceId, request }: { sequenceId: string; request: IncrementStartRequest }) =>
      runTransaction(context("start-increment", operationId), (transactionId) =>
        drybulkCargoOperationsService.startIncrement(operationId, sequenceId, request, {
          transactionId,
        }),
      ),
    onSuccess: refresh,
  });
  const complete = useMutation({
    mutationFn: ({
      sequenceId,
      incrementId,
      request,
    }: {
      sequenceId: string;
      incrementId: string;
      request: IncrementCompleteRequest;
    }) =>
      runTransaction(context("complete-increment", operationId), (transactionId) =>
        drybulkCargoOperationsService.completeIncrement(
          operationId,
          sequenceId,
          incrementId,
          request,
          { transactionId },
        ),
      ),
    onSuccess: refresh,
  });
  return { start, complete };
};
